/**
 * Math behind the calculator widgets, kept free of React so it can be tested directly.
 */

export interface RetirementPlanInputs {
  currentAge: number;
  retirementAge: number;
  currentSavings: number;
  monthlyContribution: number;
  annualReturn: number;
  inflation: number;
  desiredIncome: number;
}

export const retirementPlan = ({
  currentAge, retirementAge, currentSavings, monthlyContribution, annualReturn, inflation, desiredIncome,
}: RetirementPlanInputs) => {
  const yearsToRetire = Math.max(retirementAge - currentAge, 1);
  const monthlyRate = annualReturn / 100 / 12;
  const months = yearsToRetire * 12;

  // Future value of current savings + monthly contributions
  // Handle 0% annual return (no compounding, just sum contributions)
  const fv = monthlyRate === 0
    ? currentSavings + monthlyContribution * months
    : currentSavings * Math.pow(1 + monthlyRate, months) +
      monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);

  // Inflation-adjusted value
  const inflationFactor = Math.pow(1 + inflation / 100, yearsToRetire);
  const todaysDollars = fv / inflationFactor;

  // 4% rule: annual income in retirement
  const monthlyIncome = (fv * 0.04) / 12;
  const annualIncome = fv * 0.04;

  // How much needed for desired income (inflation-adjusted)
  const inflatedDesiredIncome = desiredIncome * inflationFactor;
  const neededAtRetirement = inflatedDesiredIncome / 0.04;

  const hasGoal = neededAtRetirement > 0;
  const onTrack = hasGoal && fv >= neededAtRetirement;
  const gap = neededAtRetirement - fv;
  const progress = neededAtRetirement > 0
    ? Math.min((fv / neededAtRetirement) * 100, 100)
    : 0;

  // Years of retirement funded (assuming 4% withdrawal)
  const yearsFunded = fv > 0 && inflatedDesiredIncome > 0
    ? Math.round(fv / inflatedDesiredIncome)
    : 0;

  return { fv, todaysDollars, monthlyIncome, annualIncome, hasGoal, onTrack, gap, progress, yearsFunded, neededAtRetirement };
};

export interface CompoundMilestone { year: number; contributions: number; interest: number; balance: number }

export const compoundGrowth = (initial: number, monthly: number, rate: number, years: number) => {
  const r = rate / 100 / 12;
  const n = years * 12;
  const futureValue = r !== 0
    ? initial * Math.pow(1 + r, n) + monthly * ((Math.pow(1 + r, n) - 1) / r)
    : initial + monthly * n;
  const totalContributions = initial + monthly * n;
  const totalInterest = futureValue - totalContributions;

  const milestones: CompoundMilestone[] = [];
  for (let y = 5; y <= years; y += 5) {
    const months = y * 12;
    const bal = r !== 0
      ? initial * Math.pow(1 + r, months) + monthly * ((Math.pow(1 + r, months) - 1) / r)
      : initial + monthly * months;
    const cont = initial + monthly * months;
    milestones.push({ year: y, contributions: cont, interest: bal - cont, balance: bal });
  }
  if (years % 5 !== 0 && years > 0) {
    milestones.push({ year: years, contributions: totalContributions, interest: totalInterest, balance: futureValue });
  }

  const contributionPct = futureValue > 0 ? Math.min((totalContributions / futureValue) * 100, 100) : 0;

  return { futureValue, totalContributions, totalInterest, milestones, contributionPct };
};

export interface FireInputs {
  annualExpenses: number;
  investedAssets: number;
  annualSavings: number;
  returnRate: number;
  swr: number;
}

/**
 * yearsToFire is null until expenses and a withdrawal rate are entered, 0 when
 * already there, and -1 when it never happens within 100 years.
 */
export const fireResults = ({ annualExpenses, investedAssets, annualSavings, returnRate, swr }: FireInputs) => {
  const withdrawal = swr > 0 ? swr / 100 : 0;
  const fireNumber = withdrawal ? annualExpenses / withdrawal : 0;
  const progress = fireNumber > 0 ? Math.min((investedAssets / fireNumber) * 100, 100) : 0;

  let yearsToFire: number | null = 0;
  if (fireNumber <= 0) {
    yearsToFire = null;
  } else if (investedAssets >= fireNumber) {
    yearsToFire = 0;
  } else if (annualSavings <= 0) {
    yearsToFire = -1;
  } else {
    const r = returnRate / 100;
    let balance = investedAssets;
    let years = 0;
    while (balance < fireNumber && years < 100) {
      balance = balance * (1 + r) + annualSavings;
      years++;
    }
    yearsToFire = years >= 100 ? -1 : years;
  }

  // Coast FIRE: amount needed today that grows to fireNumber by age 65 (assume 35 years)
  const coastYears = 35;
  const coastFire = fireNumber / Math.pow(1 + returnRate / 100, coastYears);

  const leanFire = withdrawal ? (annualExpenses * 0.75) / withdrawal : 0;
  const fatFire = withdrawal ? (annualExpenses * 1.5) / withdrawal : 0;

  return { fireNumber, progress, yearsToFire, coastFire, leanFire, fatFire };
};

export interface Debt {
  name: string;
  balance: number;
  apr: number;
  minPayment: number;
}

export const DEBT_PAYOFF_MAX_MONTHS = 600;

export const simulateDebtPayoff = (debts: Debt[], extraPayment: number, strategy: 'snowball' | 'avalanche') => {
  if (debts.length === 0 || debts.every(d => d.balance <= 0)) {
    return { months: 0, totalInterest: 0, payoffOrder: [] as string[] };
  }

  const balances = debts.map(d => d.balance);
  let totalInterest = 0;
  let months = 0;
  const paidOff: number[] = [];
  const markPaid = (i: number) => {
    balances[i] = 0;
    if (!paidOff.includes(i)) paidOff.push(i);
  };

  while (balances.some(b => b > 0) && months < DEBT_PAYOFF_MAX_MONTHS) {
    months++;

    for (let i = 0; i < debts.length; i++) {
      if (balances[i] > 0) {
        const monthlyRate = debts[i].apr / 100 / 12;
        const interest = balances[i] * monthlyRate;
        totalInterest += interest;
        balances[i] += interest;
      }
    }

    // Minimums from debts already paid off roll over to the rest.
    let freed = extraPayment + paidOff.reduce((sum, i) => sum + debts[i].minPayment, 0);
    for (let i = 0; i < debts.length; i++) {
      if (balances[i] > 0) {
        const payment = Math.min(debts[i].minPayment, balances[i]);
        balances[i] -= payment;
        if (balances[i] <= 0.01) {
          markPaid(i);
          freed += debts[i].minPayment - payment;
        }
      }
    }

    if (freed > 0) {
      const indices = debts.map((_, i) => i).filter(i => balances[i] > 0);
      if (strategy === 'snowball') {
        indices.sort((a, b) => balances[a] - balances[b]);
      } else {
        indices.sort((a, b) => debts[b].apr - debts[a].apr);
      }
      for (const idx of indices) {
        if (freed <= 0) break;
        const payment = Math.min(freed, balances[idx]);
        balances[idx] -= payment;
        freed -= payment;
        if (balances[idx] <= 0.01) markPaid(idx);
      }
    }
  }

  return { months, totalInterest, payoffOrder: paidOff.map(i => debts[i].name) };
};

export interface ProjectionRow {
  year: number;
  openingBalance: number;
  appreciation: number;
  spending: number;
  closingBalance: number;
}

export interface ProjectionInputs {
  holdings: Array<{ totalValue: number; cagr: number }>;
  currentYear: number;
  retirementYear: number;
  annualSpending: number;
  inflationRate: number;
  taxRate: number;
}

export const PROJECTION_MAX_YEARS = 50;
/** Years simulated after retirement before calling the money "indefinite". */
export const PROJECTION_RETIREMENT_YEARS = 50;
const PROJECTION_HARD_CAP = 150;

// Math notes:
// - Annual Spending is entered in TODAY's dollars
// - Each year, we inflate spending by `(1 + inflationRate)^y` so purchasing power is preserved
// - Spending starts AT retirement year (>=) — first retirement year spending = annualSpending * (1+inflation)^yearsUntilRetirement
// - Each year: opening balance → apply growth → (capture nest egg if retiring) → deduct spending → closing balance
export const projectRetirement = ({
  holdings, currentYear, retirementYear, annualSpending, inflationRate, taxRate,
}: ProjectionInputs) => {
  const yearsUntilRetirement = Math.max(retirementYear - currentYear, 0);
  const rows: ProjectionRow[] = [];
  const values = holdings.map(h => h.totalValue);

  let portfolioAtRetirement = 0;
  let depletionYear: number | null = null;

  const maxYears = Math.min(
    Math.max(PROJECTION_MAX_YEARS, yearsUntilRetirement + PROJECTION_RETIREMENT_YEARS),
    PROJECTION_HARD_CAP,
  );
  for (let y = 0; y < maxYears; y++) {
    const openingBalance = values.reduce((s, v) => s + v, 0);
    if (openingBalance <= 0 && y > 0) {
      depletionYear = depletionYear || (currentYear + y);
      break;
    }

    for (let i = 0; i < values.length; i++) {
      values[i] = values[i] * (1 + holdings[i].cagr / 100);
    }

    const balanceAfterGrowth = values.reduce((s, v) => s + v, 0);
    const appreciation = balanceAfterGrowth - openingBalance;

    // Capture nest egg at retirement (after growth, before spending)
    if (y === yearsUntilRetirement) {
      portfolioAtRetirement = balanceAfterGrowth;
    }

    // Spending starts AT retirement year, inflated from today's dollars, and
    // grossed up for tax: the portfolio must pay out the pre-tax amount.
    const isRetired = (currentYear + y) >= retirementYear;
    const taxFactor = taxRate > 0 && taxRate < 100 ? 1 - taxRate / 100 : 1;
    const spending = isRetired
      ? (annualSpending * Math.pow(1 + inflationRate / 100, y)) / taxFactor
      : 0;

    if (spending > 0 && balanceAfterGrowth > 0) {
      const ratio = Math.min(spending / balanceAfterGrowth, 1);
      for (let i = 0; i < values.length; i++) {
        values[i] = values[i] * (1 - ratio);
      }
    }

    const closingBalance = Math.max(values.reduce((s, v) => s + v, 0), 0);

    rows.push({ year: currentYear + y, openingBalance, appreciation, spending, closingBalance });

    if (closingBalance <= 0) {
      depletionYear = currentYear + y;
      break;
    }
  }

  // Fallback if retirement is beyond projection window
  if (!portfolioAtRetirement && rows.length > 0) {
    portfolioAtRetirement = rows[Math.min(yearsUntilRetirement, rows.length - 1)]?.closingBalance || 0;
  }

  return { rows, portfolioAtRetirement, depletionYear, yearsUntilRetirement };
};
